import "./searchInput.css";
import { useEffect, useRef, useState } from "react";
import { apiGet } from "@/client/serverApi";
import ItemResult from "./ItemResult";
import { useNavigate } from "react-router";
import { useAuctionHouseCtx } from "@/client/contexts/AuctionHouseProvider";

const SearchInput = ({
    initValue,
    onChange,
}: {
    initValue?: string;
    /**
     * Fired when input changed.
     * @param term The search term.
     * @returns false if default behavior should be supressed (No dropdown search results).
     */
    onChange?: (term: string) => boolean;
}): JSX.Element => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState<{ isDefault: boolean; term: string }>({ isDefault: true, term: "" });
    const [searchResults, setSearchResults] = useState<ApiItemSearchResponse | undefined>();
    const inputField = useRef<HTMLInputElement>(null);
    const { selectedAuctionHouse } = useAuctionHouseCtx();

    useEffect(() => {
        if (!selectedAuctionHouse || !searchTerm || searchTerm.term.length < 3 || searchTerm.isDefault) {
            return setSearchResults(undefined);
        }

        const searchTimer = setTimeout(() => {
            if (onChange && !onChange(searchTerm.term)) return;
            apiGet<ApiItemSearchResponse>(`/api/search/item/${searchTerm.term}`).then((res) => {
                if (res.error) {
                    alert(`Failed to get search results: ${res.error.message}`);
                    return;
                }
                setSearchResults(res.data);
            });
        }, 500);

        return () => clearTimeout(searchTimer);
    }, [searchTerm]);

    const resultItems: JSX.Element[] = [];
    if (searchResults) {
        for (const sr of searchResults.items) {
            resultItems.push(
                <ItemResult
                    key={sr.itemId}
                    itemId={sr.itemId}
                    name={sr.itemName}
                    quality={sr.qualityId}
                    icon={sr.iconName}
                ></ItemResult>
            );
        }
        if (searchResults.haveMore)
            resultItems.push(
                <span key="havemore" className="itemSearchHaveMore">
                    ...
                </span>
            );
    }

    useEffect(() => {
        const onClickHandler = () => {
            if (searchResults) setSearchResults(undefined);
        };
        window.addEventListener("click", onClickHandler);
        return () => window.removeEventListener("click", onClickHandler);
    });

    const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();
        if (!selectedAuctionHouse) return;
        const value = inputField.current!.value;
        if (value.length < 3) return;
        setSearchResults(undefined);
        navigate(`/search/${inputField.current!.value}`);
    };

    if (searchTerm.isDefault && searchTerm.term == "" && initValue && initValue.length > 2) {
        setSearchTerm({ isDefault: true, term: initValue });
    }

    return (
        <div className="itemSearch">
            <div className="itemSearchWrap">
                <form onSubmit={onSubmit}>
                    <input
                        ref={inputField}
                        className="itemSearchInput"
                        type="text"
                        placeholder="Search itemname..."
                        value={searchTerm?.term}
                        onChange={(e) => setSearchTerm({ isDefault: false, term: e.target.value })}
                    ></input>
                </form>
                {searchResults ? <div className="itemSearchResultWrap">{resultItems}</div> : null}
            </div>
        </div>
    );
};

export default SearchInput;
